<template>
    <div class="report-page">

      <div class="info-card">
        <h2>📝 Your Exposure Journey</h2>
        <p>
          Select a date range to generate a personal progress report of all your exposures.
          This can help you and your therapist understand your progress and challenges.
        </p>
      </div>

      <p v-if="validationError" class="error-message">{{ validationError }}</p>

      <div class="report-inputs">

        <div class="form-group">
          <label for="from-date-input">From</label>
          <div class="date-input-wrapper">
            <input id="from-date-input" v-model="from_date" type="date" />
            <span class="calendar-icon">
              <font-awesome-icon :icon="['fas', 'calendar-alt']" />
            </span>
          </div>
          <small class="input-hint">Tap to select a date</small>
        </div>
        <div class="form-group">
          <label for="to-date-input">To</label>
          <div class="date-input-wrapper">
            <input id="to-date-input" v-model="to_date" type="date" />
            <span class="calendar-icon">
              <font-awesome-icon :icon="['fas', 'calendar-alt']" />
            </span>
          </div>
          <small class="input-hint">Tap to select a date</small>
        </div>

        <button @click="generateSummary" class="button primary">
          <span v-if="!loading">Create Report</span>
          <span v-if="loading">Generating your report...</span>
        </button>

      </div>

      <div id="summary_document_container" style="display: none;">
          <div id="summary_document">
            <div class="document_header">
                <div>
                    <img :src="logoImage" />
                </div>
                <div>
                    <h1>Exposure Summary Report</h1>
                    <h2>{{ formatDateRange(from_date, to_date) }}</h2>
                </div>
            </div>
            <div class="document_body">
                <!-- <p>Total amount of exposures:<br>{{ getAmountOfExposures() }}</p> -->

                <div class="report_stats_info">
                  <div>
                    <p>{{ getAmountOfExposures() }}</p>
                    <small>Exposures Completed</small>
                  </div>
                  <div>
                    <p>{{ getAmountOfDifferentFoods() }}</p>
                    <small>Dishes Tackled</small>
                  </div>
                  <div>
                    <p>{{ getAverageAnxiety() }}</p>
                    <small>Avg. Anxiety (1–10)</small>
                  </div>
                </div>

                <div v-for="(food, index) in reportData" class="food_entry">
                    <p>{{ food.title }}</p>
                    <div class="table-wrapper">
                        <table class="summary_table">
                          <thead>
                              <tr>
                                  <th>Date</th>
                                  <th>Anxiety Level</th>
                                  <th>Taste</th>
                                  <th class="big-col">Bodys reaction</th>
                                  <th class="big-col">Additional notes</th>
                              </tr>
                          </thead>

                          <tbody>
                              <tr v-for="log in food.logs">
                                  <td>{{log.date}}</td>
                                  <td>{{log.anxiety_level}}</td>
                                  <td>{{log.taste}}</td>
                                  <td>{{log.bodys_reaction}}</td>
                                  <td>{{log.additional_notes}}</td>
                              </tr>
                          </tbody>
                      </table>
                    </div>
                    <hr v-if="index !== reportData.length -1">
                </div>
            </div>
            <small>Created with Brave Bites</small>
        </div>
      </div>

    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useStore } from 'vuex';
import { fetchWithAuth } from '@/utils/api';
import html2pdf from 'html2pdf.js';
import logoImage from '@/assets/images/brave_bites_new_logo.png';

const store = useStore();
const apiUrl = store.getters.getApiUrl;

const loading = ref(false);
const from_date = ref("");
const to_date = ref("");
const validationError = ref("");


const reportData = ref([]);

const generateSummary = async () => {
  if (!from_date.value || !to_date.value) {
    validationError.value = "Both start and end dates must be provided.";
    return;
  }
  validationError.value = "";

  try {
    await fetchReportData();
    // Automatically generate and download the PDF after data is ready
    setTimeout(() => {
      generatePdf();
    }, 500); // slight delay ensures DOM has rendered
  } catch (error) {
    console.error('Error fetching data:', error);
  }
};


function clear() {
  reportData.value = [];
}

const generatePdf = () => {
  const container = document.getElementById('summary_document_container');
  const element = document.getElementById('summary_document');
  if (!container || !element) return;

  // Add pdf-exporting class to hide the container but keep it in the DOM
  container.classList.add('pdf-exporting');

  const opt = {
    margin: 0.5,
    filename: `exposure-summary-${from_date.value}_to_${to_date.value}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true },
    jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
    pagebreak: {
      mode: ['css', 'legacy']
    }
  };

  html2pdf()
    .set(opt)
    .from(element)
    .outputPdf('blob') // Generate PDF as a Blob instead of auto-saving
    .then((pdfBlob) => {
      // Create a blob URL for download
      const blobUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = opt.filename;

      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      container.classList.remove('pdf-exporting');
    });
};


const getAverageAnxiety = () => {
  const exposuresAmount = getAmountOfExposures();

  let anxietyTotal = 0;

  reportData.value.forEach(food => {
    food.logs.forEach(log => {
      let temp = 0;
      temp += Number(log.anxiety_level);
      anxietyTotal += temp;
    });
  });

  return (anxietyTotal / exposuresAmount).toFixed(1);
}

const getAmountOfExposures = () => {
  let totalExposures = 0;

  reportData.value.forEach(food => {
    totalExposures += food.logs.length;
  });

  return totalExposures;
};

const getAmountOfDifferentFoods = () => {
  return reportData.value.length;
}

function formatDateRange(fromDateStr: string | null, toDateStr: string | null) {
  if (!fromDateStr || !toDateStr) {
    return '';
  }

  const fromDate = new Date(fromDateStr);
  const toDate = new Date(toDateStr);

  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const yearOptions: Intl.DateTimeFormatOptions = { year: 'numeric' };

  const sameMonth = fromDate.getMonth() === toDate.getMonth();
  const sameYear = fromDate.getFullYear() === toDate.getFullYear();

  const fromFormatted = fromDate.toLocaleDateString('en-US', options);
  const toFormatted = toDate.toLocaleDateString('en-US', options);
  const year = toDate.toLocaleDateString('en-US', yearOptions);

  if (sameMonth && sameYear) {
    return `${fromFormatted} – ${toFormatted}, ${year}`;
  } else if (sameYear) {
    const fromWithMonth = fromDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const toWithMonth = toDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${fromWithMonth} – ${toWithMonth}, ${year}`;
  } else {
    const fromFull = fromDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const toFull = toDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${fromFull} – ${toFull}`;
  }
}


const fetchReportData = async () => {
  if (!from_date.value || !to_date.value) {
    alert('Start date and end date must be provided');
    return;
  }

  loading.value = true;

  try {
    const response = await fetchWithAuth(apiUrl + `/api/dishes/report?start_date=${from_date.value}&end_date=${to_date.value}`, {
      method: 'GET'
    }, store);
    if (response.ok) {
      const data = await response.json();
      reportData.value = data;
    } else {
      alert("Error while fetching report data");
      console.error('Failed to fetch report data:', await response.text());
    }

    loading.value = false;

  } catch (error) {
    alert("Error while fetching report data");
    loading.value = false;
  }
};

</script>

<style scoped>

.report-inputs {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: var(--space-md);
}

.report-page > h1 {
  margin-bottom: var(--space-md);
}

.report-page > p {
  font-weight: 300;
}


.report {
  padding: 25px 10px 0px;
}

.form_row {
  display: flex;
  gap: 10px;
  width: 100%;
  margin-top: 10px;
}

.form_col small {
  font-size: .7rem;
  color: grey;
}

.form_col small span {
  color: rgb(78, 78, 78);
}

.form_col {
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
}

.form_col input {
  width: 100%;
}

.form_col label {
  font-size: .8rem;
}

.period-selection {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.period-selection > h1 {
    margin-bottom: 0;
    font-size: 1.5rem;
}

.period-selection > div > p {
    font-weight: 300;
}

.period-selection > div {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.period-selection button {
    height: 40px;
}

.before_summary {
    display: flex;
    flex-direction: column;
}

.before_summary > div {
  display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    margin: 0 0 10px 0;
    width: 1000px;
}

#summary_document {
    background-color: white;
    padding: 40px;
    border-radius: var(--border-radius-lg);
    display: flex;
    flex-direction: column;
    gap: 40px;
    width: 700px;
    box-shadow: 0 8px 30px -5px rgba(0, 0, 0, 0.1);
    margin-top: 20px;
    /* height: 1414.214px; */
}

.document_header {
    display: flex;
    flex-direction: row;
    gap: 40px;
}

.document_header > div {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.document_header > div > img {
    width: 90px;
    height: auto;
}

.document_header h1, .document_header h2 {
    margin: 0;
}

.document_header h1 {
    font-size: 35px;
    margin-top: 10px;
}

.document_header h2 {
    font-size: 1rem;
    font-weight: 400;
}

#summary_document small {
    font-weight: 200;
    font-size: .6rem;
}

.summary_table {
    width: 100%;
    max-width: 700px;
    border-collapse: collapse;
}

.summary_table thead, .summary_table tbody {
    width: 100%;
}

.summary_table th, .summary_table td {
    padding: 10px;
    text-align: left;
    font-size: .5rem;
    max-width: 50px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    white-space: normal;
    word-break: break-word; 
    vertical-align: top;
}

.summary_table tr:nth-child(even) {
  background-color: #f9f9f9;
}

.summary_table th {
  background-color: #eee;
  font-weight: 600;
}

.big-col {
    width: 200px !important;
    max-width: 200px !important;
}

.document_body {
    display: flex;
    flex-direction: column;
}

.document_body hr {
    margin: 20px 0;
    border: 1px solid rgb(219, 219, 219);
}

.food_entry {
  page-break-inside: avoid;
  break-inside: avoid;
  page-break-before: auto;
  break-before: auto;
  margin-bottom: 30px;
}

.food_entry > p {
    font-weight: 600;
    margin-bottom: 10px;
    font-size: 1rem;
}

.report_stats_info {
  padding: 20px;
  background-color: hsl(24, 100%, 95%);
  border-radius: 5px;
  margin-bottom: 20px;
  font-size: .8rem;
  display: flex;
  flex-direction: row;
  gap: 10px;
  justify-content: space-around;
}

.report_stats_info > div {
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: center;
}

.report_stats_info > div > p {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: hsl(24, 100%, 45%);
}

.report_stats_info > div > small {
  font-size: .8rem;
  font-weight: 300 !important;
  color: hsl(24, 100%, 45%);
}

.info-card {
  background-color: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.table-wrapper {
  overflow: hidden; /* Needed to clip the corners */
  border-radius: 8px; /* Match your stats box or choose your radius */
  border: 1px solid #ddd;
  margin-bottom: 20px;
}

.summary_table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.date-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}
.date-input-wrapper input[type='date'] {
  padding-right: 2em;
  width: 100%;
  box-sizing: border-box;
}
.calendar-icon {
  position: absolute;
  right: 0.5em;
  pointer-events: none;
  color: #aaa;
}
.input-hint {
  font-size: 0.8em;
  color: #888;
}

.document_header,
.report_stats_info {
  page-break-after: avoid;
  break-after: avoid;
}

.pdf-exporting {
  visibility: hidden !important;
  position: absolute !important;
  left: -9999px !important;
  top: 0 !important;
  width: 0 !important;
  height: 0 !important;
  pointer-events: none !important;
  z-index: -1 !important;
}

</style>
  